import React, { useMemo, useEffect, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { CascadingSelectValuePair } from '@atlassian/jira-issue-field-cascading-select-editview-full';
import { CASCADING_SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { useCascadingSelectField } from '../../../common/cascading-select';
import { CellWrapper } from '../../../common/cell-wrapper';
import type {
	TransformedCascadingSelectField,
	CascadingSelectFieldValue,
} from '../../../common/field-types/cascading-select/types';
import type { FieldCellProps, BasicListViewItem } from '../../../common/types';
import { CascadingSelectEdit } from './edit/async';
import { CascadingSelectRead } from './read';

export const CascadingSelectCell = ({
	rowData: issue,
	cellData: field,
	setEditMode,
	editMode,
	column: { width: preferredWidth },
	cellRef,
}: FieldCellProps<BasicListViewItem, TransformedCascadingSelectField>) => {
	const [cascadingSelectValue, setCascadingSelectValue] = useState<
		CascadingSelectFieldValue | CascadingSelectValuePair | null
	>(field?.value ?? null);

	// Update the value when it is updated in cache (e.g. via realtime)
	useEffect(() => {
		setCascadingSelectValue(field?.value ?? null);
	}, [field?.value]);

	const isEditable = useMemo(
		() => issue.fieldConfig?.isEditable || false,
		[issue.fieldConfig?.isEditable],
	);

	if (fg('jwm_list_new_copy_paste_behaviour')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [{ value }] = useCascadingSelectField({
			fieldAriId: field?.id ?? '',
			issueKey: issue.key,
			fieldKey: field?.fieldId ?? '',
			initialValue: field?.value ?? null,
		});

		if (value !== cascadingSelectValue) {
			setCascadingSelectValue(value);
		}
	}

	return (
		<CellWrapper
			isEditable={isEditable}
			testId="business-fields.ui.cascading-select.cell.cell-wrapper"
		>
			{editMode ? (
				<CascadingSelectEdit
					cellRef={cellRef}
					setEditMode={setEditMode}
					fieldAriId={field?.id ?? ''}
					onChange={setCascadingSelectValue}
					preferredWidth={preferredWidth}
					readContent={<CascadingSelectRead value={cascadingSelectValue} />}
					currentValue={cascadingSelectValue}
					issueKey={issue.key}
					fieldKey={field?.fieldId || ''}
				/>
			) : (
				<CascadingSelectRead value={cascadingSelectValue} />
			)}
		</CellWrapper>
	);
};

export const cascadingSelectCellHandler = {
	[CASCADING_SELECT_CF_TYPE]: CascadingSelectCell,
};
